"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';
import {
  BeakerIcon,
  ShoppingBagIcon,
  BanknotesIcon,
  TruckIcon,
  CogIcon,
  HeartIcon,
  FireIcon,
  BoltIcon,
  SignalIcon,
  CubeIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  ChartBarIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isIndustriesOpen, setIsIndustriesOpen] = useState(false);

  const industries = [
    { name: 'Pharmaceutical', slug: 'pharmaceutical', icon: BeakerIcon },
    { name: 'Retail & Restaurant Chains', slug: 'retail', icon: ShoppingBagIcon },
    { name: 'Financial Services', slug: 'financial-services', icon: BanknotesIcon },
    { name: 'Logistics & Supply Chain', slug: 'logistics', icon: TruckIcon },
    { name: 'Manufacturing', slug: 'manufacturing', icon: CogIcon },
    { name: 'Healthcare', slug: 'healthcare', icon: HeartIcon },
    { name: 'Oil & Gas', slug: 'oil-gas', icon: FireIcon },
    { name: 'Energy & Utilities', slug: 'energy-utilities', icon: BoltIcon },
    { name: 'Telecommunications', slug: 'telecommunications', icon: SignalIcon },
    { name: 'CPG / FMCG', slug: 'cpg-fmcg', icon: CubeIcon },
    { name: 'Hospitality & Travel', slug: 'hospitality-travel', icon: GlobeAltIcon },
    { name: 'Insurance', slug: 'insurance', icon: ShieldCheckIcon },
    { name: 'Transportation', slug: 'transportation', icon: TruckIcon },
    { name: 'Education', slug: 'education', icon: AcademicCapIcon },
    { name: 'Market Research', slug: 'market-research', icon: ChartBarIcon },
    { name: 'Technology', slug: 'technology', icon: CpuChipIcon },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-lg border-b border-white/10' : 'shadow-none border-b border-transparent'
      }`}
      style={{ backgroundColor: '#1B2845' }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <Image
            src="/assets/images/All-SIMAX-LOGOS_-_9_-_Edited-1770650153046.png"
            alt="Simax Systems Logo"
            width={140}
            height={140}
            className="object-contain"
            priority
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8 text-sm font-medium tracking-wide">
          <Link
            href="/homepage"
            className="text-white hover:text-secondary transition-colors uppercase"
          >
            HOME
          </Link>
          <Link
            href="/solutions"
            className="text-white hover:text-secondary transition-colors uppercase"
          >
            SOLUTIONS
          </Link>
          
          {/* Industries Dropdown */}
          <div 
            className="relative group"
            onMouseEnter={() => setIsIndustriesOpen(true)}
            onMouseLeave={() => setIsIndustriesOpen(false)}
          >
            <button
              className="text-white hover:text-secondary transition-colors uppercase flex items-center gap-1"
            >
              INDUSTRIES
              <Icon name="ChevronDownIcon" size={16} className="transition-transform duration-200 group-hover:rotate-180" />
            </button>
            
            {/* Dropdown Menu - solid dark bg so content is visible on any page background */}
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[600px] rounded-lg shadow-2xl transition-all duration-300 border border-white/10 ${
                isIndustriesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}
              style={{ backgroundColor: '#1B2845' }}
            >
              <div className="grid grid-cols-2 gap-1 p-4">
                {industries?.map((industry) => {
                  const IconComponent = industry?.icon;
                  return (
                    <Link
                      key={industry?.slug}
                      href={`/industries/${industry?.slug}`}
                      className="px-4 py-3 text-white hover:text-secondary hover:bg-white/10 rounded transition-all duration-200 text-sm flex items-center gap-3"
                    >
                      <IconComponent className="w-5 h-5 text-secondary flex-shrink-0" />
                      <span>{industry?.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          <Link
            href="/engagement-models"
            className="text-white hover:text-secondary transition-colors uppercase"
          >
            ENGAGEMENT MODELS
          </Link>
          <Link
            href="/our-approach"
            className="text-white hover:text-secondary transition-colors uppercase"
          >
            OUR APPROACH
          </Link>
          <Link
            href="/blog"
            className="text-white hover:text-secondary transition-colors uppercase"
          >
            BLOG
          </Link>
          <Link
            href="/contact"
            className="text-white hover:text-secondary transition-colors uppercase"
          >
            CONTACT
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden text-white"
          aria-label="Toggle menu"
        >
          <Icon
            name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
            size={24}
          />
        </button>
      </div>
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-white/10 shadow-lg max-h-[80vh] overflow-y-auto" style={{ backgroundColor: '#1B2845' }}>
          <div className="px-6 py-4 space-y-4">
            <Link
              href="/homepage"
              className="block text-white hover:text-secondary transition-colors py-2 uppercase"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              HOME
            </Link>
            <Link
              href="/solutions"
              className="block text-white hover:text-secondary transition-colors py-2 uppercase"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              SOLUTIONS
            </Link>
            
            {/* Mobile Industries Accordion */}
            <div>
              <button
                onClick={() => setIsIndustriesOpen(!isIndustriesOpen)}
                className="w-full flex items-center justify-between text-white hover:text-secondary transition-colors py-2 uppercase"
              >
                INDUSTRIES
                <Icon 
                  name="ChevronDownIcon" 
                  size={16} 
                  className={`transition-transform duration-200 ${isIndustriesOpen ? 'rotate-180' : ''}`} 
                />
              </button>
              {isIndustriesOpen && (
                <div className="pl-4 mt-2 space-y-2 border-l-2 border-secondary/30">
                  {industries?.map((industry) => {
                    const IconComponent = industry?.icon;
                    return (
                      <Link
                        key={industry?.slug}
                        href={`/industries/${industry?.slug}`}
                        className="block text-white/80 hover:text-secondary transition-colors py-2 text-sm flex items-center gap-3"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <IconComponent className="w-5 h-5 text-secondary flex-shrink-0" />
                        <span>{industry?.name}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            <Link
              href="/engagement-models"
              className="block text-white hover:text-secondary transition-colors py-2 uppercase"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              ENGAGEMENT MODELS
            </Link>
            <Link
              href="/our-approach"
              className="block text-white hover:text-secondary transition-colors py-2 uppercase"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              OUR APPROACH
            </Link>
            <Link
              href="/blog"
              className="block text-white hover:text-secondary transition-colors py-2 uppercase"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              BLOG
            </Link>
            <Link
              href="/contact"
              className="block text-white hover:text-secondary transition-colors py-2 uppercase"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              CONTACT
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}